<template>
  <div class="account-shell">
    <AppTopHeader show-logout-button @logout="logout" />

    <AccountPageSkeleton v-if="accountHydrating" />

    <div v-else class="account-page">
      <div class="layout">
      <aside class="sidebar">
        <div class="profile-block">
          <div class="avatar" :class="{ 'avatar--photo': !!imageUrl }">
            <img v-if="imageUrl" :src="imageUrl" alt="" />
            <template v-else>{{ avatarLetter }}</template>
          </div>
          <div class="profile-text">
            <div class="user-name">{{ fullName || "Usuario" }}</div>
            <div class="user-email">{{ email || "sem email" }}</div>
            <div v-if="visitedCountryCodes.length" class="sidebar-flags" aria-label="Países visitados">
              <span
                v-for="code in sidebarPreviewCodes"
                :key="code"
                class="sidebar-flag"
                :title="countryTitle(code)"
              >{{ flagEmoji(code) }}</span>
              <span v-if="sidebarExtra > 0" class="sidebar-flag-more">+{{ sidebarExtra }}</span>
            </div>
          </div>
        </div>
        <button class="menu-item" :class="{ active: activeSection === 'saved' }" @click="openSavedSection">Destinos salvos</button>
        <button class="menu-item" :class="{ active: activeSection === 'profile' }" @click="activeSection = 'profile'">Minha conta</button>
        <button v-if="isAdmin" class="menu-item" :class="{ active: activeSection === 'users' }" @click="openUsersSection">Usuarios</button>
        <button v-if="isAdmin" class="menu-item" :class="{ active: activeSection === 'destinations' }" @click="openDestinationsSection">Destinations</button>
      </aside>

      <main class="content">
        <h1>{{ titleBySection }}</h1>
        <p class="subtitle">{{ subtitleBySection }}</p>

        <template v-if="activeSection === 'profile'">
        <VisitedCountriesSection v-model="visitedCountryCodes" :save-error="visitCountriesSaveError" />
        <section class="panel">
          <h2>Informacoes Pessoais</h2>
          <div class="avatar-edit-row">
            <div class="avatar avatar-lg" :class="{ 'avatar--photo': !!imageUrl }">
              <img v-if="imageUrl" :src="imageUrl" alt="" />
              <template v-else>{{ avatarLetter }}</template>
            </div>
            <div class="avatar-edit-actions">
              <input
                ref="avatarFileInput"
                type="file"
                class="visually-hidden"
                accept="image/jpeg,image/png,image/gif,image/webp"
                @change="onAvatarFileChange"
              />
              <button
                type="button"
                class="btn ghost"
                :disabled="avatarUploadBusy"
                @click="$refs.avatarFileInput.click()"
              >
                {{ avatarUploadBusy ? "A enviar…" : "Escolher foto do computador" }}
              </button>
              <button
                v-if="imageUrl"
                type="button"
                class="mini-btn"
                :disabled="avatarUploadBusy"
                @click="removeProfilePhoto"
              >
                Remover foto
              </button>
              <p class="avatar-hint">JPEG, PNG, GIF ou WebP — máx. 5 MB.</p>
            </div>
          </div>
          <form @submit.prevent="saveChanges">
            <div class="grid">
              <div class="field field-full"><label>Nome completo</label><input v-model="fullName" type="text" /></div>
              <div class="field"><label>Email</label><input :value="email" type="email" disabled /></div>
              <div class="field">
                <label>CPF</label>
                <input v-model="cpf" type="text" :disabled="isCpfLocked" @input="onCpfInput" :class="{ 'input-error': cpfError }" />
                <small v-if="cpfError" class="inline-error">{{ cpfError }}</small>
              </div>
              <div class="field"><label>Telefone</label><input v-model="phone" type="text" @input="onPhoneInput" /></div>
              <div class="field"><label>Data de aniversario</label><input v-model="birthday" type="text" @input="onBirthdayInput" /></div>
              <div class="field field-full"><label>Usuario</label><input :value="usernameDisplay" type="text" disabled /></div>
            </div>
            <div class="actions">
              <p v-if="formError" class="form-error">{{ formError }}</p>
              <button type="submit" class="btn primary">Salvar alteracoes</button>
            </div>
          </form>
        </section>
        </template>

        <section v-else-if="activeSection === 'saved'" class="panel">
          <div class="cards">
            <article v-for="city in savedDestinations" :key="city.id" class="card clickable" @click="$router.push(`/city/${city.id}`)">
              <img :src="city.imageUrl" :alt="city.name" />
              <h3>{{ city.name }}, {{ city.country }}</h3>
              <small>R$ {{ formatCurrency(city.monthlyCost) }} / mes</small>
            </article>
          </div>
          <p v-if="!savedDestinations.length">Voce ainda nao salvou destinos.</p>
        </section>

        <section v-else-if="activeSection === 'users'" class="panel">
          <table class="users-table">
            <thead><tr><th>Nome</th><th>E-mail</th><th>CPF</th><th>Usuario</th><th>Admin</th><th>Acoes</th></tr></thead>
            <tbody>
              <tr v-for="user in users" :key="user.id">
                <td>{{ user.fullName || "-" }}</td><td>{{ user.email }}</td><td>{{ formatCpf(user.cpf || "") || "-" }}</td><td>{{ user.username }}</td>
                <td>{{ user.isAdmin ? "Sim" : "Nao" }}</td>
                <td>
                  <button class="mini-btn" @click="startUserEdit(user)">Editar</button>
                  <button class="mini-btn" @click="toggleAdmin(user)">{{ user.isAdmin ? "Remover admin" : "Tornar admin" }}</button>
                </td>
              </tr>
            </tbody>
          </table>
          <form v-if="editingUser" class="edit-box" @submit.prevent="saveUserEdit">
            <h3>Editando: {{ editingUser.email }}</h3>
            <div class="grid">
              <div class="field field-full"><label>Nome completo</label><input v-model="editingUser.fullName" type="text" /></div>
              <div class="field"><label>E-mail</label><input v-model="editingUser.email" type="email" required /></div>
              <div class="field"><label>Usuario</label><input v-model="editingUser.username" type="text" required /></div>
              <div class="field"><label>CPF</label><input v-model="editingUser.cpf" type="text" @input="onEditingCpfInput" /></div>
              <div class="field"><label>Telefone</label><input v-model="editingUser.phone" type="text" @input="onEditingPhoneInput" /></div>
              <div class="field"><label>Data de aniversario</label><input v-model="editingUser.birthday" type="text" @input="onEditingBirthdayInput" /></div>
            </div>
            <div class="actions"><button type="button" class="btn ghost" @click="cancelUserEdit">Cancelar</button><button type="submit" class="btn primary">Salvar</button></div>
          </form>
        </section>

        <section v-else class="panel">
          <div class="actions"><button class="btn primary" @click="newDestination">Nova cidade</button></div>
          <table class="users-table">
            <thead><tr><th>Nome</th><th>Pais</th><th>Custo</th><th>Score</th><th>Acoes</th></tr></thead>
            <tbody>
              <tr v-for="city in destinations" :key="city.id">
                <td>{{ city.name }}</td><td>{{ city.country }}</td><td>R$ {{ formatCurrency(city.monthlyCost) }}</td><td>{{ Number(city.scoreOverall || 0).toFixed(1) }}</td>
                <td>
                  <button class="mini-btn" @click="openCityEditor(city)">Abrir na cidade</button>
                  <button class="mini-btn" @click="editDestination(city)">Formulario</button>
                  <button class="mini-btn danger" @click="deleteDestination(city)">Excluir</button>
                </td>
              </tr>
            </tbody>
          </table>

          <form v-if="editingDestination" class="edit-box" @submit.prevent="saveDestination">
            <h3>{{ editingDestination.id ? "Editar cidade" : "Nova cidade" }}</h3>
            <div v-if="editingDestination.id" class="ai-draft-row">
              <button
                type="button"
                class="mini-btn"
                :disabled="aiDraftBusy"
                @click="populateDestinationWithAI"
              >
                {{ aiDraftBusy ? "A gerar rascunho…" : "Popular com IA" }}
              </button>
              <button
                type="button"
                class="mini-btn"
                @click="importJsonModalOpen = true"
              >
                Importar JSON
              </button>
              <p class="ai-draft-hint">
                Preenche o formulário com um rascunho gerado no servidor (chave OpenAI ou API compatível). Confira dados
                sensíveis (vistos, preços) antes de salvar.
              </p>
            </div>
            <div v-if="importJsonModalOpen" class="import-json-modal">
              <h4>Importar dados via JSON</h4>
              <p class="ai-draft-hint">Cole o JSON abaixo ou carregue um ficheiro <code>.json</code>.</p>
              <input
                type="file"
                accept=".json"
                style="margin-bottom:8px;"
                @change="onImportJsonFile"
              />
              <textarea
                v-model="importJsonText"
                rows="8"
                style="width:100%;font-family:monospace;font-size:12px;"
                :placeholder='`{\n  \"name\": \"Ksamil\",\n  \"country\": \"Albania\",\n  \"countryCode\": \"AL\",\n  \"monthlyCost\": 800,\n  \"description\": \"...\",\n  \"pros\": [\"Praia linda\", \"Barato\"],\n  \"cons\": [\"Pouca infraestrutura\"],\n  \"scoreOverall\": 7.5,\n  \"scoreCost\": 9,\n  \"scoreInternet\": 6,\n  \"tags\": [\"Beach\", \"Cheap\"]\n}`'
              />
              <p v-if="importJsonError" class="inline-error" style="margin-top:4px;">{{ importJsonError }}</p>
              <div class="actions" style="margin-top:8px;">
                <button type="button" class="btn primary" @click="applyImportedJson">Aplicar</button>
                <button type="button" class="btn ghost" @click="importJsonModalOpen = false; importJsonError = ''">Cancelar</button>
              </div>
            </div>
            <div class="grid">
              <LocationSelectFields
                class="loc-grid-span"
                :city-name="editingDestination.name"
                :country-label="editingDestination.country"
                :country-code="editingDestination.countryCode || ''"
                @update:city-name="editingDestination.name = $event"
                @update:country-label="editingDestination.country = $event"
                @update:country-code="editingDestination.countryCode = $event"
              />
              <div class="field field-full"><small class="name-hint">Nome da cidade (obrigatório) vem da busca acima ou digitação livre.</small></div>
              <div class="field"><label>Regiao</label><input v-model="editingDestination.region" /></div>
              <div class="field field-full">
                <label>Imagem da cidade</label>
                <div class="dest-image-row">
                  <img
                    v-if="editingDestination.imageUrl"
                    :src="editingDestination.imageUrl"
                    alt=""
                    class="dest-image-preview"
                  />
                  <div class="dest-image-controls">
                    <input
                      v-model="editingDestination.imageUrl"
                      type="url"
                      placeholder="Colar URL externa ou enviar ficheiro"
                    />
                    <div class="dest-image-actions">
                      <label class="mini-btn">
                        {{ destinationImageBusy ? "A enviar…" : "Enviar ficheiro" }}
                        <input
                          type="file"
                          accept="image/*"
                          :disabled="destinationImageBusy"
                          hidden
                          @change="onDestinationImageSelected"
                        />
                      </label>
                      <button
                        v-if="editingDestination.imageUrl"
                        type="button"
                        class="mini-btn ghost"
                        :disabled="destinationImageBusy"
                        @click="editingDestination.imageUrl = ''"
                      >
                        Remover
                      </button>
                    </div>
                    <small v-if="destinationImageError" class="dest-image-error">{{ destinationImageError }}</small>
                  </div>
                </div>
              </div>
              <div class="field"><label>Custo mensal *</label><input v-model.number="editingDestination.monthlyCost" type="number" min="1" required /></div>
              <div class="field"><label>Score geral (0-10)</label><input v-model.number="editingDestination.scoreOverall" type="number" min="0" max="10" step="0.1" /></div>
              <div class="field"><label>Score custo (0-10)</label><input v-model.number="editingDestination.scoreCost" type="number" min="0" max="10" step="0.1" /></div>
              <div class="field"><label>Score internet (0-10)</label><input v-model.number="editingDestination.scoreInternet" type="number" min="0" max="10" step="0.1" /></div>
              <div class="field field-full">
                <label>Pros</label>
                <div class="chips"><span v-for="(item, idx) in editingDestination.pros" :key="`pro-${idx}`" class="chip">{{ item }} <button type="button" @click="removePro(idx)">x</button></span></div>
                <div class="inline-row"><input v-model="proInput" placeholder="Adicionar pro" /><button type="button" class="mini-btn" @click="addPro">+ adicionar item</button></div>
              </div>
              <div class="field field-full">
                <label>Cons</label>
                <div class="chips"><span v-for="(item, idx) in editingDestination.cons" :key="`con-${idx}`" class="chip">{{ item }} <button type="button" @click="removeCon(idx)">x</button></span></div>
                <div class="inline-row"><input v-model="conInput" placeholder="Adicionar contra" /><button type="button" class="mini-btn" @click="addCon">+ adicionar item</button></div>
              </div>
              <div class="field field-full">
                <label>Tags</label>
                <div class="tag-select">
                  <label v-for="tag in availableTags" :key="tag"><input type="checkbox" :value="tag" v-model="editingDestination.tags" /> {{ tag }}</label>
                </div>
              </div>
              <div class="field field-full">
                <label><input v-model="editingDestination.communityEnabled" type="checkbox" /> Comunidade ativa na lista (/communities)</label>
              </div>
              <div class="field field-full"><label>Descrição da comunidade</label><textarea v-model="editingDestination.communityDescription" rows="2" /></div>
              <div class="field field-full">
                <label>Regras da comunidade (uma por linha; vazio = padrão)</label>
                <textarea v-model="editingDestinationCommunityRulesText" rows="3" />
              </div>
            </div>
            <div class="field field-full">
              <label>Secções customizadas</label>
              <CityCustomSectionsEditor v-model="editingDestination.customSections" />
            </div>
            <div class="actions"><button type="button" class="btn ghost" @click="editingDestination = null">Cancelar</button><button type="submit" class="btn primary">Salvar cidade</button></div>
          </form>
        </section>
      </main>
      </div>
    </div>
    <div v-if="toastMessage" class="toast-success">{{ toastMessage }}</div>

    <CommunityImageCropModal
      :open="cropModalOpen"
      :image-src="cropObjectURL"
      :aspect-ratio="1"
      :max-crop-width="800"
      :max-crop-height="800"
      hint-text="Enquadre o rosto no quadrado — corresponde à área da foto redonda do perfil. Arraste e redimensione o quadro antes de usar."
      @close="onAvatarCropClose"
      @confirm="onAvatarCropConfirm"
    />
  </div>
</template>

<script>
import axios from "axios";
import LocationSelectFields from "../components/LocationSelectFields.vue";
import AppTopHeader from "../components/AppTopHeader.vue";
import AccountPageSkeleton from "../components/skeleton/AccountPageSkeleton.vue";
import VisitedCountriesSection from "../components/VisitedCountriesSection.vue";
import CommunityImageCropModal from "../components/CommunityImageCropModal.vue";
import CityCustomSectionsEditor from "@/components/CityCustomSectionsEditor.vue";
import { countryNamePt, flagEmojiFromAlpha2 } from "../utils/countryDisplay";
import { normalizeDestination, buildDestinationAdminPayload, mergeAiCityDraft } from "../utils/destinationPayload";
import { uploadImageFile } from "../utils/uploadMedia";
import { flipmemoApiBase, flipmemoAiDraftPostUrls, flipmemoPublicPingUrl } from "@/config/flipmemoApi";

export default {
  name: "MyAccountPageV2",
  components: { LocationSelectFields, AppTopHeader, AccountPageSkeleton, VisitedCountriesSection, CommunityImageCropModal, CityCustomSectionsEditor },
  data() {
    return {
      accountHydrating: true,
      fullName: "", email: "", cpf: "", phone: "", birthday: "", username: "",       imageUrl: "",
      avatarUploadBusy: false,
      cropModalOpen: false,
      cropObjectURL: "",
      cpfInitiallySet: false, cpfError: "", formError: "", isAdmin: false, activeSection: "saved",
      users: [], editingUser: null, savedDestinations: [], destinations: [],
      editingDestination: null, proInput: "", conInput: "", toastMessage: "",
      destinationImageBusy: false, destinationImageError: "",
      availableTags: ["Cheap", "Beach", "Fast internet", "City"],
      visitedCountryCodes: [],
      visitCountriesHydrating: true,
      visitCountriesPersistTimer: null,
      visitCountriesSaveError: "",
      aiDraftBusy: false,
      importJsonModalOpen: false,
      importJsonText: "",
      importJsonError: "",
    };
  },
  watch: {
    visitedCountryCodes: {
      deep: true,
      handler() {
        if (this.visitCountriesHydrating) return;
        this.visitCountriesSaveError = "";
        if (this.visitCountriesPersistTimer) clearTimeout(this.visitCountriesPersistTimer);
        this.visitCountriesPersistTimer = setTimeout(() => {
          this.visitCountriesPersistTimer = null;
          this.persistVisitedCountries();
        }, 400);
      },
    },
  },
  computed: {
    avatarLetter() { return (this.fullName || this.username || "U").charAt(0).toUpperCase(); },
    usernameDisplay() { return this.username?.startsWith("@") ? this.username : `@${this.username || ""}`; },
    isCpfLocked() { return this.cpfInitiallySet; },
    titleBySection() {
      if (this.activeSection === "users") return "Usuarios";
      if (this.activeSection === "destinations") return "Destinations";
      if (this.activeSection === "saved") return "Destinos salvos";
      return "Minha Conta";
    },
    subtitleBySection() {
      if (this.activeSection === "users") return "Gerencie usuarios e perfis admin.";
      if (this.activeSection === "destinations") return "Gerencie cidades e insights do produto.";
      if (this.activeSection === "saved") return "Destinos favoritos da sua conta.";
      return "Gerencie seus dados de perfil.";
    },
    sidebarPreviewCodes() {
      return (this.visitedCountryCodes || []).slice(0, 5);
    },
    sidebarExtra() {
      const n = (this.visitedCountryCodes || []).length;
      return n > 5 ? n - 5 : 0;
    },
    editingDestinationCommunityRulesText: {
      get() {
        if (!this.editingDestination) return "";
        return (this.editingDestination.communityRules || []).join("\n");
      },
      set(v) {
        if (!this.editingDestination) return;
        this.editingDestination.communityRules = String(v || "")
          .split("\n")
          .map((l) => l.trim())
          .filter(Boolean);
      },
    },
  },
  async mounted() {
    try {
      await this.fetchUserData();
      await this.fetchSavedDestinations();
    } finally {
      this.accountHydrating = false;
    }
  },
  beforeUnmount() {
    if (this.visitCountriesPersistTimer) clearTimeout(this.visitCountriesPersistTimer);
  },
  methods: {
    apiBase() { return flipmemoApiBase; },
    authHeaders() { return { Authorization: `Bearer ${localStorage.getItem("token")}` }; },
    onlyDigits(v) { return String(v || "").replace(/\D/g, ""); },
    formatCurrency(v) { return Number(v || 0).toLocaleString("pt-BR"); },
    formatCpf(v) {
      const d = this.onlyDigits(v).slice(0, 11);
      if (d.length <= 3) return d;
      if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
      if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
      return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
    },
    formatPhone(v) {
      const d = this.onlyDigits(v).slice(0, 11);
      if (d.length <= 2) return d;
      if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
      if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
      return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
    },
    formatBirthday(v) {
      const d = this.onlyDigits(v).slice(0, 8);
      if (d.length <= 2) return d;
      if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
      return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
    },
    isValidCpf(cpf) {
      if (!/^\d{11}$/.test(cpf) || /^(\d)\1{10}$/.test(cpf)) return false;
      const calc = (base, factor) => { let t = 0; for (const n of base) t += parseInt(n, 10) * factor--; const r = (t * 10) % 11; return r === 10 ? 0 : r; };
      return calc(cpf.slice(0, 9), 10) === parseInt(cpf[9], 10) && calc(cpf.slice(0, 10), 11) === parseInt(cpf[10], 10);
    },
    showToast(msg) { this.toastMessage = msg; setTimeout(() => { this.toastMessage = ""; }, 2500); },
    flagEmoji(code) { return flagEmojiFromAlpha2(code); },
    countryTitle(code) { return countryNamePt(code); },
    onCpfInput() { this.cpf = this.formatCpf(this.cpf); },
    onPhoneInput() { this.phone = this.formatPhone(this.phone); },
    onBirthdayInput() { this.birthday = this.formatBirthday(this.birthday); },
    onAvatarFileChange(ev) {
      const f = ev.target?.files?.[0];
      ev.target.value = "";
      if (!f) return;
      if (this.cropObjectURL) {
        URL.revokeObjectURL(this.cropObjectURL);
        this.cropObjectURL = "";
      }
      this.cropObjectURL = URL.createObjectURL(f);
      this.cropModalOpen = true;
    },
    onAvatarCropClose() {
      this.cropModalOpen = false;
      if (this.cropObjectURL) {
        URL.revokeObjectURL(this.cropObjectURL);
        this.cropObjectURL = "";
      }
    },
    async onAvatarCropConfirm(blob) {
      if (!blob) return;
      const file = new File([blob], "perfil.jpg", { type: "image/jpeg" });
      await this.uploadProfilePhoto(file);
    },
    async uploadProfilePhoto(file) {
      const token = localStorage.getItem("token");
      if (!token) return;
      this.avatarUploadBusy = true;
      this.formError = "";
      try {
        const url = await uploadImageFile(file, "avatar", token);
        await axios.put(`${this.apiBase()}/user`, { imageUrl: url }, { headers: this.authHeaders() });
        this.imageUrl = url;
        this.showToast("Foto de perfil atualizada.");
      } catch (error) {
        const message = error?.response?.data?.error || error?.message || "Falha ao enviar a foto.";
        this.formError = message;
        this.showToast(String(message));
      } finally {
        this.avatarUploadBusy = false;
      }
    },
    async removeProfilePhoto() {
      this.formError = "";
      try {
        await axios.put(`${this.apiBase()}/user`, { imageUrl: "" }, { headers: this.authHeaders() });
        this.imageUrl = "";
        this.showToast("Foto removida.");
      } catch (error) {
        const message = error?.response?.data?.error || "Falha ao remover.";
        this.formError = message;
      }
    },
    onEditingCpfInput() { if (this.editingUser) this.editingUser.cpf = this.formatCpf(this.editingUser.cpf); },
    onEditingPhoneInput() { if (this.editingUser) this.editingUser.phone = this.formatPhone(this.editingUser.phone); },
    onEditingBirthdayInput() { if (this.editingUser) this.editingUser.birthday = this.formatBirthday(this.editingUser.birthday); },
    async fetchUserData() {
      this.visitCountriesHydrating = true;
      try {
        const response = await axios.get(`${this.apiBase()}/user`, { headers: this.authHeaders() });
        const userData = response.data;
        this.fullName = userData.FullName || "";
        this.email = userData.Email || "";
        this.cpf = this.formatCpf(userData.CPF || "");
        this.phone = this.formatPhone(userData.Phone || "");
        this.birthday = this.formatBirthday(userData.Birthday || "");
        this.username = userData.Username || "";
        this.imageUrl = (userData.imageUrl || userData.ImageUrl || "").trim();
        this.cpfInitiallySet = this.onlyDigits(userData.CPF || "").length === 11;
        this.isAdmin = !!userData.IsAdmin;
        const vc = userData.VisitedCountryCodes;
        this.visitedCountryCodes = Array.isArray(vc) ? vc.map((c) => String(c).toUpperCase()) : [];
      } finally {
        this.$nextTick(() => {
          this.visitCountriesHydrating = false;
        });
      }
    },
    async persistVisitedCountries() {
      if (this.visitCountriesHydrating) return;
      this.visitCountriesSaveError = "";
      try {
        await axios.put(
          `${this.apiBase()}/user`,
          { VisitedCountryCodes: [...this.visitedCountryCodes] },
          { headers: this.authHeaders() },
        );
      } catch (error) {
        const message = error?.response?.data?.error || "Não foi possível salvar os países.";
        this.visitCountriesSaveError = message;
        this.showToast(String(message));
        await this.fetchUserData();
      }
    },
    async saveChanges() {
      if (this.visitCountriesPersistTimer) {
        clearTimeout(this.visitCountriesPersistTimer);
        this.visitCountriesPersistTimer = null;
      }
      this.cpfError = ""; this.formError = "";
      const cpfDigits = this.onlyDigits(this.cpf);
      if (!this.cpfInitiallySet && cpfDigits.length > 0 && !this.isValidCpf(cpfDigits)) { this.cpfError = "CPF invalido."; return; }
      const payload = {
        FullName: this.fullName,
        Phone: this.phone,
        Birthday: this.birthday,
        VisitedCountryCodes: [...this.visitedCountryCodes],
      };
      if (!this.cpfInitiallySet && cpfDigits.length === 11) payload.CPF = cpfDigits;
      try {
        await axios.put(`${this.apiBase()}/user`, payload, { headers: this.authHeaders() });
        this.visitCountriesSaveError = "";
        await this.fetchUserData(); this.showToast("Dados salvos com sucesso.");
      } catch (error) {
        const message = error?.response?.data?.error || "Falha ao salvar.";
        if (String(message).toLowerCase().includes("cpf")) this.cpfError = message; else this.formError = message;
      }
    },
    async openSavedSection() { this.activeSection = "saved"; await this.fetchSavedDestinations(); },
    async fetchSavedDestinations() {
      const response = await axios.get(`${this.apiBase()}/saved-destinations`, { headers: this.authHeaders() });
      this.savedDestinations = response.data || [];
    },
    async openUsersSection() { this.activeSection = "users"; await this.fetchUsers(); },
    async fetchUsers() {
      const response = await axios.get(`${this.apiBase()}/admin/users`, { headers: this.authHeaders() });
      this.users = response.data || [];
    },
    startUserEdit(user) {
      this.editingUser = { id: user.id, fullName: user.fullName || "", email: user.email || "", username: user.username || "", cpf: this.formatCpf(user.cpf || ""), phone: this.formatPhone(user.phone || ""), birthday: this.formatBirthday(user.birthday || "") };
    },
    cancelUserEdit() { this.editingUser = null; },
    async saveUserEdit() {
      const payload = { fullName: this.editingUser.fullName, email: this.editingUser.email, username: this.editingUser.username, cpf: this.onlyDigits(this.editingUser.cpf), phone: this.editingUser.phone, birthday: this.editingUser.birthday };
      await axios.put(`${this.apiBase()}/admin/users/${this.editingUser.id}`, payload, { headers: this.authHeaders() });
      await this.fetchUsers(); this.showToast("Usuario atualizado."); this.editingUser = null;
    },
    async toggleAdmin(user) {
      await axios.put(`${this.apiBase()}/admin/users/${user.id}/admin`, { isAdmin: !user.isAdmin }, { headers: this.authHeaders() });
      await this.fetchUsers(); this.showToast("Permissao atualizada.");
    },
    async openDestinationsSection() { this.activeSection = "destinations"; await this.fetchDestinations(); },
    async fetchDestinations() {
      const response = await axios.get(`${this.apiBase()}/destinations`, { headers: this.authHeaders() });
      this.destinations = response.data || [];
    },
    newDestination() {
      this.editingDestination = normalizeDestination({
        name: "",
        country: "",
        countryCode: "",
        region: "",
        imageUrl: "",
        monthlyCost: 2500,
        scoreOverall: 7,
        scoreCost: 7,
        scoreInternet: 7,
        pros: [],
        cons: [],
        tags: [],
        descriptionEn: "",
        prosEn: [],
        consEn: [],
      });
      this.proInput = "";
      this.conInput = "";
    },
    editDestination(city) {
      this.editingDestination = normalizeDestination(JSON.parse(JSON.stringify(city)));
      this.proInput = "";
      this.conInput = "";
    },
    openCityEditor(city) {
      this.$router.push({ path: `/city/${city.id}`, query: { edit: "1" } });
    },
    addPro() { if (!this.proInput.trim()) return; this.editingDestination.pros.push(this.proInput.trim()); this.proInput = ""; },
    addCon() { if (!this.conInput.trim()) return; this.editingDestination.cons.push(this.conInput.trim()); this.conInput = ""; },
    removePro(i) { this.editingDestination.pros.splice(i, 1); },
    removeCon(i) { this.editingDestination.cons.splice(i, 1); },
    onImportJsonFile(event) {
      const file = event.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (e) => { this.importJsonText = e.target.result; };
      reader.readAsText(file);
    },
    applyImportedJson() {
      try {
        const data = JSON.parse(this.importJsonText);
        if (!data.name || !data.monthlyCost || data.monthlyCost <= 0) {
          this.importJsonError = 'JSON inválido: "name" e "monthlyCost" são obrigatórios.';
          return;
        }
        const fields = ['name','country','countryCode','region','description','monthlyCost',
                        'scoreOverall','scoreCost','scoreInternet','imageUrl',
                        'descriptionEn','communityDescription'];
        fields.forEach(f => { if (data[f] !== undefined) this.editingDestination[f] = data[f]; });
        if (Array.isArray(data.pros)) this.editingDestination.pros = data.pros;
        if (Array.isArray(data.cons)) this.editingDestination.cons = data.cons;
        if (Array.isArray(data.prosEn)) this.editingDestination.prosEn = data.prosEn;
        if (Array.isArray(data.consEn)) this.editingDestination.consEn = data.consEn;
        if (Array.isArray(data.tags)) this.editingDestination.tags = data.tags;
        this.importJsonModalOpen = false;
        this.importJsonText = "";
        this.importJsonError = "";
      } catch (e) {
        this.importJsonError = 'JSON inválido: ' + e.message;
      }
    },
    async aiDraft404Hint() {
      const pingUrl = flipmemoPublicPingUrl();
      try {
        const { data } = await axios.get(pingUrl, { timeout: 8000 });
        if (data?.service === "flipmemo-backend" && data?.features?.adminDestinationAiDraft === true) {
          return "O ping indica backend com IA, mas o POST devolveu 404. Verifique proxy ou CDN; confira as URLs tentadas no console.";
        }
        return "Backend sem indicação de rascunho IA no ping — faça deploy do flipmemo-backend atual e confira VUE_APP_API_BASE_URL no build.";
      } catch {
        return "Não foi possível contactar o ping da API. Confira VUE_APP_API_BASE_URL (host do flipmemo-backend) e CORS.";
      }
    },
    async populateDestinationWithAI() {
      if (!this.editingDestination?.id) {
        this.showToast("Guarde a cidade primeiro para poder usar a IA.");
        return;
      }
      this.aiDraftBusy = true;
      try {
        const urls = flipmemoAiDraftPostUrls(this.editingDestination.id);
        let data;
        let lastErr;
        for (let i = 0; i < urls.length; i++) {
          try {
            const res = await axios.post(urls[i], {}, { headers: this.authHeaders(), timeout: 200000 });
            data = res.data;
            break;
          } catch (err) {
            lastErr = err;
            if (err?.response?.status === 404 && i < urls.length - 1) continue;
            throw err;
          }
        }
        if (!data) throw lastErr;
        if (!data?.draft || typeof data.draft !== "object") {
          this.showToast("Resposta inválida da API.");
          return;
        }
        this.editingDestination = mergeAiCityDraft(this.editingDestination, data.draft);
        this.showToast("Rascunho aplicado. Revise o conteúdo e clique em Salvar cidade.");
      } catch (err) {
        const status = err?.response?.status;
        const apiErr = err?.response?.data?.error;
        let msg = apiErr || err?.message || "Falha ao gerar rascunho.";
        if (status) msg = `${msg} (HTTP ${status})`;
        if (status === 404) {
          msg = await this.aiDraft404Hint();
        }
        if (err?.code === "ECONNABORTED" || String(err?.message || "").includes("timeout")) {
          msg = "Tempo esgotado — a IA ou o Commons demoraram demais. Tenta outra vez.";
        }
        this.showToast(String(msg));
        console.error("ai-draft", {
          apiBase: this.apiBase(),
          destinationId: this.editingDestination?.id,
          triedUrls: this.editingDestination?.id ? flipmemoAiDraftPostUrls(this.editingDestination.id) : [],
          pingUrl: flipmemoPublicPingUrl(),
          err,
        });
      } finally {
        this.aiDraftBusy = false;
      }
    },
    async onDestinationImageSelected(event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = "";
      if (!file || !this.editingDestination) return;
      const token = localStorage.getItem("token");
      if (!token) {
        this.destinationImageError = "Sessao expirada. Faca login novamente.";
        return;
      }
      this.destinationImageBusy = true;
      this.destinationImageError = "";
      try {
        const url = await uploadImageFile(file, "destination", token);
        this.editingDestination.imageUrl = url;
        this.showToast("Imagem enviada.");
      } catch (error) {
        const message = error?.response?.data?.error || error?.message || "Falha ao enviar a imagem.";
        this.destinationImageError = String(message);
        this.showToast(String(message));
      } finally {
        this.destinationImageBusy = false;
      }
    },
    async saveDestination() {
      const payload = buildDestinationAdminPayload(this.editingDestination, { syncProsConsToInsights: true });
      if (!payload || !payload.name || payload.monthlyCost <= 0) {
        this.showToast("Nome e custo sao obrigatorios.");
        return;
      }
      try {
        if (this.editingDestination.id) {
          await axios.put(`${this.apiBase()}/admin/destinations/${this.editingDestination.id}`, payload, { headers: this.authHeaders() });
        } else {
          await axios.post(`${this.apiBase()}/admin/destinations`, payload, { headers: this.authHeaders() });
        }
        this.editingDestination = null;
        await this.fetchDestinations();
        this.showToast("Destino salvo.");
      } catch (err) {
        const api = err?.response?.data?.error;
        const msg = api || err?.message || "Nao foi possivel salvar.";
        this.showToast(String(msg));
      }
    },
    async deleteDestination(city) {
      if (!window.confirm(`Excluir ${city.name}?`)) return;
      await axios.delete(`${this.apiBase()}/admin/destinations/${city.id}`, { headers: this.authHeaders() });
      await this.fetchDestinations(); this.showToast("Destino excluido.");
    },
    logout() {
      localStorage.removeItem("token"); localStorage.removeItem("username"); localStorage.removeItem("userId");
      this.$store.commit("setUsername", null); this.$router.push("/login");
    },
  },
};
</script>

<style scoped>
.account-shell {
  min-height: 100vh;
  min-height: 100dvh;
  background: #0d1b2e;
  color: #e2eaf4;
  font-family: Inter, "Segoe UI", Roboto, sans-serif;
}
/* Conteúdo abaixo do header fixo (66px): scroll no documento, sem flex no mesmo bloco do header */
.account-page {
  padding-top: 96px;
  padding-bottom: 40px;
}
.layout {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 20px;
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: 24px;
  align-items: start;
}
.sidebar {
  background: #152338;
  border: 1px solid #1e3352;
  border-radius: 14px;
  padding: 16px;
}
.profile-block { display: flex; gap: 12px; align-items: flex-start; padding-bottom: 14px; border-bottom: 1px solid #1e3352; }
.profile-text { min-width: 0; flex: 1; }
.sidebar-flags {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  margin-top: 8px;
}
.sidebar-flag {
  font-size: 1.15rem;
  line-height: 1;
}
.sidebar-flag-more {
  font-size: 11px;
  font-weight: 800;
  color: #7eb3ff;
  margin-left: 2px;
}
.avatar { width: 44px; height: 44px; border-radius: 999px; background: #4f8ef7; color: #fff; font-weight: 800; display: flex; align-items: center; justify-content: center; flex-shrink: 0; overflow: hidden; }
.avatar--photo { background: #1e3352; color: #94adc8; }
.avatar--photo img { width: 100%; height: 100%; object-fit: cover; }
.avatar-lg { width: 72px; height: 72px; font-size: 1.35rem; }
.avatar-edit-row { display: flex; gap: 16px; align-items: flex-start; margin-bottom: 20px; padding-bottom: 18px; border-bottom: 1px solid #1e3352; }
.avatar-edit-actions { display: flex; flex-direction: column; align-items: flex-start; gap: 8px; min-width: 0; }
.avatar-hint { margin: 0; font-size: 12px; color: #4a6080; line-height: 1.4; max-width: 420px; }
.visually-hidden { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0; }
.user-name { font-weight: 700; } .user-email { color: #94adc8; font-size: 12px; }
.menu-item { width: 100%; border: none; text-align: left; background: transparent; margin-top: 14px; padding: 10px 12px; border-radius: 10px; color: #94adc8; cursor: pointer; }
.menu-item.active { background: #1e3a8a; color: #93c5fd; font-weight: 700; }
.content h1 { margin: 0; font-size: 40px; letter-spacing: -0.03em; } .subtitle { margin: 8px 0 20px; color: #94adc8; }
.panel {
  background: #152338;
  border: 1px solid #1e3352;
  border-radius: 14px;
  padding: 22px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}
.grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; }
.loc-grid-span { grid-column: 1 / -1; }
.name-hint { color: #4a6080; font-size: 12px; margin: -6px 0 0; }
.field { display: flex; flex-direction: column; gap: 6px; } .field-full { grid-column: 1 / -1; }
label { text-transform: uppercase; letter-spacing: 0.06em; font-size: 11px; color: #94adc8; font-weight: 700; }
input { border: 1px solid #1e3352; border-radius: 10px; padding: 12px; font-size: 15px; background: #081526; color: #e2eaf4; }
input:disabled { background: #0d1b2e; color: #4a6080; }
.input-error { border-color: #f87171; box-shadow: 0 0 0 2px rgba(248, 113, 113, 0.15); }
.inline-error { color: #f87171; font-size: 12px; font-weight: 600; }
.actions { margin-top: 18px; display: flex; align-items: center; justify-content: flex-end; gap: 10px; }
.form-error { margin-right: auto; color: #f87171; font-size: 13px; font-weight: 600; }
.btn { border: none; border-radius: 10px; padding: 12px 16px; font-weight: 700; cursor: pointer; }
.btn.ghost { background: #1a2e48; color: #94adc8; } .btn.primary { background: #4f8ef7; color: #fff; }
.users-table { width: 100%; border-collapse: collapse; font-size: 14px; }
.users-table th, .users-table td { border-bottom: 1px solid #1e3352; padding: 10px 8px; text-align: left; vertical-align: middle; }
.mini-btn { border: 1px solid #1e3352; background: #152338; border-radius: 8px; padding: 6px 10px; font-size: 12px; cursor: pointer; font-weight: 600; margin-right: 6px; color: #94adc8; }
.mini-btn.danger { border-color: #7f1d1d; color: #fca5a5; }
.edit-box { margin-top: 18px; border: 1px solid #1e3352; border-radius: 12px; padding: 14px; background: #081526; }
.ai-draft-row {
  margin-bottom: 16px;
  padding-bottom: 14px;
  border-bottom: 1px solid #1e3352;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}
.import-json-modal {
  background: #0f1c2e;
  border: 1px solid #1e3352;
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 16px;
}
.import-json-modal h4 {
  margin: 0 0 6px;
  font-size: 14px;
  color: #e2e8f0;
}
.import-json-modal textarea {
  background: #152338;
  border: 1px solid #1e3352;
  border-radius: 6px;
  color: #e2e8f0;
  padding: 8px;
  resize: vertical;
}
.import-json-modal code {
  font-size: 11px;
  background: #1e3352;
  padding: 1px 4px;
  border-radius: 3px;
}
.ai-draft-hint {
  margin: 0;
  font-size: 12px;
  color: #94adc8;
  line-height: 1.45;
  max-width: 720px;
}
.cards { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; }
.card { background: #152338; border: 1px solid #1e3352; border-radius: 12px; padding: 8px; }
.card img { width: 100%; height: 160px; object-fit: cover; border-radius: 10px; }
.card h3 { margin: 8px 0 2px; font-size: 16px; } .card small { color: #94adc8; }
.clickable { cursor: pointer; }
.chips { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 8px; }
.chip { background: #1a2e48; border-radius: 999px; padding: 4px 8px; font-size: 12px; color: #94adc8; }
.chip button { margin-left: 6px; border: none; background: transparent; cursor: pointer; }
.inline-row { display: flex; gap: 8px; }
.tag-select { display: flex; gap: 10px; flex-wrap: wrap; }
.toast-success { position: fixed; right: 20px; bottom: 20px; background: #16a34a; color: #fff; padding: 12px 16px; border-radius: 10px; font-weight: 700; box-shadow: 0 10px 25px rgba(0, 0, 0, 0.12); z-index: 60; }
.dest-image-row { display: flex; gap: 12px; align-items: flex-start; }
.dest-image-preview { width: 96px; height: 96px; object-fit: cover; border-radius: 8px; border: 1px solid #e5e7eb; background: #f3f4f6; }
.dest-image-controls { flex: 1; display: flex; flex-direction: column; gap: 8px; }
.dest-image-actions { display: flex; gap: 8px; flex-wrap: wrap; }
.dest-image-actions .mini-btn { display: inline-flex; align-items: center; cursor: pointer; }
.dest-image-error { color: #b91c1c; font-weight: 600; }
@media (max-width: 900px) { .layout { grid-template-columns: 1fr; } .grid { grid-template-columns: 1fr; } .cards { grid-template-columns: 1fr; } }
</style>

